import { create } from 'zustand';
import moment from 'moment';
import { GoogleCalendarState, GoogleCalendarStore } from './types';
import api from '@/services/api';
import useToastStore from '@/store/toast';
import { ErrorResponse } from '@/utils';

const initialState: GoogleCalendarState = {
  isConnected: false,
  isInvalid: false,
  googleEmail: null,
  events: [],
  teamMembers: [],
  unconnectedVps: 0,
  teamCalendarError: null,
  loading: false,
  externalEvents: [],
  syncing: false,
};

const useGoogleCalendarStore = create<GoogleCalendarStore>((set) => ({
  ...initialState,

  checkStatus: async () => {
    try {
      const status = await api.google.getStatus();
      // EINE Definition von „verbunden" (2026-07-12): isConnected = NUTZBAR
      // (Token vorhanden UND nicht als tot markiert). Ein toter Token (invalid)
      // ist NICHT verbunden -> AppBar/Kalender behandeln ihn als getrennt und
      // bieten Reconnect an; isInvalid steuert separat den Reconnect-Banner.
      set({ isConnected: status.connected && !status.invalid, isInvalid: !!status.invalid, googleEmail: status.email });
    } catch {
      set({ isConnected: false, isInvalid: false, googleEmail: null });
    }
  },

  getEvents: async (start, end) => {
    set({ loading: true });
    try {
      const events = await api.google.getEvents(start, end);
      set({ events });
    } catch {
      set({ events: [] });
    } finally {
      set({ loading: false });
    }
  },

  getCalendarMeetings: async (start, end) => {
    set({ loading: true, teamCalendarError: null });
    try {
      const meetings = await api.meeting.getCalendarMeetings(start, end);
      set({ events: meetings, loading: false });
    } catch {
      set({ loading: false });
      useToastStore.getState().showToast('Fehler beim Laden der Meetings', 'error');
    }
  },

  getTeamMembers: async () => {
    try {
      const { members, unconnectedVps } = await api.google.getTeamConnected();
      set({ teamMembers: members, unconnectedVps });
    } catch {
      set({ teamMembers: [], unconnectedVps: 0 });
    }
  },

  getTeamEvents: async (userId, start, end) => {
    set({ loading: true, teamCalendarError: null });
    try {
      const events = await api.google.getTeamEvents(userId, start, end);
      set({ events });
    } catch (error) {
      // 409 = toter/widerrufener VP-Token — Server-Meldung anzeigen
      // (manager-kalender-ansicht.md), sonst generischer Text.
      const message = error instanceof ErrorResponse ? error.getResponseMessage() : undefined;
      set({ events: [], teamCalendarError: message ?? 'Kalender-Events konnten nicht geladen werden' });
    } finally {
      set({ loading: false });
    }
  },

  disconnect: async () => {
    try {
      await api.google.disconnect();
      set({ isConnected: false, googleEmail: null, events: [] });
    } catch {
      // keep state
    }
  },


  fetchExternalEvents: async (from, to) => {
    try {
      const events = await api.google.getExternalEvents(from, to);
      set({ externalEvents: events });
    } catch {
      useToastStore.getState().showToast('Fehler beim Laden der externen Termine', 'error');
    }
  },

  triggerSync: async () => {
    set({ syncing: true });
    try {
      const result = await api.google.triggerSync();
      useToastStore.getState().showToast(`${result.events_synced} Termine synchronisiert`, 'success');
      const start = moment().startOf('isoWeek').format('YYYY-MM-DD');
      const end = moment().endOf('isoWeek').format('YYYY-MM-DD');
      const events = await api.google.getExternalEvents(start, end);
      set({ externalEvents: events, syncing: false });
    } catch {
      useToastStore.getState().showToast('Fehler bei der Kalender-Synchronisation', 'error');
      set({ syncing: false });
    }
  },

  clearState: () => set(initialState),
}));

export default useGoogleCalendarStore;
